import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../services/inventoryApi';
import adminApi from '../../../services/adminApi';

const ReceiveOrderDialog = ({ open, onClose, onSubmit, order }) => {
  const [formData, setFormData] = useState({ receipts: [], warehouseId: '', location: '', notes: '' });
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && order) {
      loadWarehouses();
      initializeReceipts();
    }
  }, [open, order]);

  const loadWarehouses = async () => {
    try {
      const response = await adminApi.getLocations({ type: 'warehouse', limit: 1000 });
      const data = response.data?.data || response.data || [];
      setWarehouses(data);
    } catch {
      try {
        const fb = await adminApi.getLocations({ limit: 1000 });
        setWarehouses((fb.data?.data || fb.data || []).filter(l => l.type === 'warehouse'));
      } catch {
        setWarehouses([]);
      }
    }
  };

  const initializeReceipts = () => {
    if (order?.items) {
      setFormData(prev => ({
        ...prev,
        receipts: order.items.map(item => ({
          itemId: item.item._id || item.item,
          itemName: item.item?.name || 'Unknown Item',
          itemSku: item.item?.sku || '',
          orderedQuantity: item.quantity,
          quantityReceived: item.quantity,
          quantityAccepted: item.quantity,
          quantityRejected: 0,
          unitCost: item.unitPrice,
          qualityNotes: ''
        }))
      }));
    }
  };

  const handleReceiptChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      receipts: prev.receipts.map((r, i) => {
        if (i !== index) return r;
        const updated = { ...r, [field]: value };
        if (field === 'quantityReceived') { updated.quantityAccepted = value; updated.quantityRejected = 0; }
        else if (field === 'quantityAccepted') updated.quantityRejected = updated.quantityReceived - value;
        else if (field === 'quantityRejected') updated.quantityAccepted = updated.quantityReceived - value;
        return updated;
      })
    }));
  };

  const validateForm = () => {
    if (!formData.warehouseId) { toast.error('Please select a warehouse'); return false; }
    if (formData.receipts.some(r => r.quantityReceived < 0 || r.quantityAccepted < 0 || r.quantityRejected < 0 || (r.quantityAccepted + r.quantityRejected) !== r.quantityReceived)) {
      toast.error('Quantities are invalid — accepted + rejected must equal received'); return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      await onSubmit(formData);
    } catch {
      toast.error('Failed to process receipt');
    } finally {
      setLoading(false);
    }
  };

  const totalReceived = formData.receipts.reduce((s, r) => s + r.quantityReceived, 0);
  const totalAccepted = formData.receipts.reduce((s, r) => s + r.quantityAccepted, 0);
  const totalRejected = formData.receipts.reduce((s, r) => s + r.quantityRejected, 0);
  const totalValue = formData.receipts.reduce((s, r) => s + r.quantityAccepted * r.unitCost, 0);

  if (!open || !order) return null;

  const inputClass = "w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">
                  Receive Order — {order.orderNumber}
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 pt-3 pb-2 space-y-3">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-2">
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            {/* Warehouse + Location */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Receiving Warehouse <span className="text-red-500">*</span></label>
                <select
                  value={formData.warehouseId}
                  onChange={(e) => setFormData(p => ({ ...p, warehouseId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">Select warehouse</option>
                  {warehouses.map(w => (
                    <option key={w._id} value={w._id}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Location</label>
                <input
                  value={formData.location}
                  onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))}
                  placeholder="Specific location within warehouse"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Items Table */}
            <div>
              <p className={labelClass}>Items to Receive</p>
              <div className="border border-gray-200 rounded overflow-x-auto">
                <table className="min-w-max w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">SKU</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Ordered</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Received</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Accepted</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Rejected</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Unit Cost</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Quality Notes</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {formData.receipts.map((receipt, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs font-medium text-gray-900">{receipt.itemName}</td>
                        <td className="px-3 py-2 text-xs text-gray-500 font-mono">{receipt.itemSku}</td>
                        <td className="px-3 py-2 text-xs text-gray-900 text-right">{receipt.orderedQuantity}</td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            value={receipt.quantityReceived}
                            onChange={(e) => handleReceiptChange(index, 'quantityReceived', parseFloat(e.target.value) || 0)}
                            min="0" step="0.01"
                            className="w-16 px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            value={receipt.quantityAccepted}
                            onChange={(e) => handleReceiptChange(index, 'quantityAccepted', parseFloat(e.target.value) || 0)}
                            min="0" max={receipt.quantityReceived} step="0.01"
                            className="w-16 px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            value={receipt.quantityRejected}
                            onChange={(e) => handleReceiptChange(index, 'quantityRejected', parseFloat(e.target.value) || 0)}
                            min="0" max={receipt.quantityReceived} step="0.01"
                            className="w-16 px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            value={receipt.unitCost}
                            onChange={(e) => handleReceiptChange(index, 'unitCost', parseFloat(e.target.value) || 0)}
                            min="0" step="0.01"
                            className="w-20 px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={receipt.qualityNotes}
                            onChange={(e) => handleReceiptChange(index, 'qualityNotes', e.target.value)}
                            placeholder="Notes"
                            className="w-32 px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary row */}
              <div className="mt-2 grid grid-cols-4 gap-2">
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded">
                  <p className="text-xs text-gray-500">Total Received</p>
                  <p className="text-sm font-semibold text-gray-900">{totalReceived}</p>
                </div>
                <div className="px-3 py-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-xs text-gray-500">Total Accepted</p>
                  <p className="text-sm font-semibold text-green-700">{totalAccepted}</p>
                </div>
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-xs text-gray-500">Total Rejected</p>
                  <p className="text-sm font-semibold text-red-700">{totalRejected}</p>
                </div>
                <div className="px-3 py-2 bg-indigo-50 border border-indigo-200 rounded">
                  <p className="text-xs text-gray-500">Total Value</p>
                  <p className="text-sm font-semibold text-indigo-700">{totalValue.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className={labelClass}>Receipt Notes</label>
              <textarea
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Any additional notes about this receipt..."
                className={inputClass}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <>
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : 'Process Receipt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiveOrderDialog;
